import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";

import useApi from "../../hooks/useApi";
import Page from "./Page";
import Loading from "../ui/Loading";

const MessageInbox = () => {
  const navigate = useNavigate();
  const { getData } = useApi();

  const [messages, setMessages] = useState([]);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [threadDetails, setThreadDetails] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInbox();
  }, []);

  const loadInbox = async () => {
    setLoading(true);
    const response = await getData("Message", "getRepliesInbox");
    if (response.isSuccessful) {
      setMessages(response.data);
    }
    setLoading(false);
  };

  const loadThread = async (messageId) => {
    if (threadDetails[messageId]) {
      // Already loaded, just toggle
      toggleThread(messageId);
      return;
    }

    const response = await getData("Message", "getMessageThread", {
      messageId,
    });

    if (response.isSuccessful) {
      setThreadDetails((prev) => ({
        ...prev,
        [messageId]: response.data,
      }));
      toggleThread(messageId);
    }
  };

  const toggleThread = (messageId) => {
    setExpandedThreads((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  const handleBack = () => {
    navigate("/messages");
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTargetingDisplay = (message) => {
    const targets = [];
    if (message.userId) targets.push("User");
    if (message.clientId) targets.push("Client");
    if (message.projectId) targets.push("Project");
    if (message.province) targets.push(`Province: ${message.province}`);
    if (message.bannerId) targets.push("Banner");
    if (message.retailerId) targets.push("Retailer");
    return targets.length > 0 ? targets.join(", ") : "All";
  };

  const renderThread = (messageId) => {
    const thread = threadDetails[messageId];
    if (!thread) return null;

    const parentMessage = thread.find((m) => !m.parentMessageId);
    const replies = thread.filter((m) => m.parentMessageId);

    return (
      <Box sx={{ p: 2, bgcolor: "grey.50" }}>
        {/* Parent Message */}
        <Paper
          sx={{ p: 2, mb: 2 }}
          elevation={0}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            gutterBottom
          >
            Original Message
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 1, fontWeight: "bold" }}
          >
            {parentMessage?.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 1, whiteSpace: "pre-wrap" }}
          >
            {parentMessage?.text}
          </Typography>
          {parentMessage?.text_fr && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", fontStyle: "italic" }}
            >
              {parentMessage?.text_fr}
            </Typography>
          )}
          {parentMessage?.images && parentMessage?.images.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Images:
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                {parentMessage?.images.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`Attachment ${i + 1}`}
                    style={{
                      maxWidth: "120px",
                      maxHeight: "120px",
                      borderRadius: "4px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          {parentMessage?.images_fr && parentMessage?.images_fr.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Images (French):
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                {parentMessage?.images_fr.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`French Attachment ${i + 1}`}
                    style={{
                      maxWidth: "120px",
                      maxHeight: "120px",
                      borderRadius: "4px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
            <Chip
              label={parentMessage?.priority}
              size="small"
              color={
                parentMessage?.priority === "urgent"
                  ? "error"
                  : parentMessage?.priority === "high"
                  ? "warning"
                  : "default"
              }
            />
            <Typography
              variant="caption"
              color="text.secondary"
            >
              by {parentMessage?.createdByName} on{" "}
              {formatDate(parentMessage?.createdAt)}
            </Typography>
          </Box>
        </Paper>

        {/* Replies */}
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          Replies ({replies.length})
        </Typography>
        {replies.map((reply, index) => (
          <Paper
            key={reply._id}
            sx={{
              p: 2,
              mb: 1,
              ml: 3,
              borderLeft: "3px solid",
              borderColor: "primary.main",
            }}
            elevation={0}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography
                variant="subtitle2"
                color="primary"
              >
                {reply.createdByName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                {formatDate(reply.createdAt)}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {reply.text}
            </Typography>
            {reply.text_fr && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, whiteSpace: "pre-wrap", fontStyle: "italic" }}
              >
                {reply.text_fr}
              </Typography>
            )}
            {reply.images && reply.images.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Images:
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}
                >
                  {reply.images.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={`Reply attachment ${i + 1}`}
                      style={{
                        maxWidth: "100px",
                        maxHeight: "100px",
                        borderRadius: "4px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {reply.images_fr && reply.images_fr.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Images (French):
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}
                >
                  {reply.images_fr.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={`French reply attachment ${i + 1}`}
                      style={{
                        maxWidth: "100px",
                        maxHeight: "100px",
                        borderRadius: "4px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Page
        pageName="repliesInbox"
        backPath="/communication"
      >
        <Loading />
      </Page>
    );
  }

  return (
    <Page
      pageName="repliesInbox"
      backPath="/communication"
    >
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
        >
          Back to Messages
        </Button>
      </Box>

      {messages.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            No messages with replies found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Targeting</TableCell>
                <TableCell align="center">Replies</TableCell>
                <TableCell>Last Reply</TableCell>
                <TableCell>Created By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((message) => (
                <>
                  <TableRow
                    key={message._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => loadThread(message._id)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedThreads[message._id] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>{message.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={message.priority}
                        size="small"
                        color={
                          message.priority === "urgent"
                            ? "error"
                            : message.priority === "high"
                            ? "warning"
                            : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {getTargetingDisplay(message)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={message.replyCount}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {formatDate(message.lastReplyDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>{message.createdByName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ p: 0 }}
                    >
                      <Collapse
                        in={expandedThreads[message._id]}
                        timeout="auto"
                        unmountOnExit
                      >
                        {renderThread(message._id)}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Page>
  );
};

export default MessageInbox;
